// How remembered gear gets back in when the browser can't hand it over (#169).
//
// Chrome on Android keeps getDevices() behind chrome://flags, and without
// persistent permissions the grant ends when Chrome closes. Either way the
// only route back is the picker, which needs a tap per device.

/**
 * Whether only the picker can bring this remembered device back: gear from
 * another phone, a browser with no getDevices(), or a getDevices() list that
 * came back without it. `grantedIds` is null until a pass has asked.
 *
 * @param {{ id: string, foreign?: boolean }} device
 * @param {{ listable: boolean, grantedIds: Set<string>|null }} browser
 */
export const needsPicker = (device, { listable, grantedIds }) =>
  !!device.foreign || !listable || (grantedIds !== null && !grantedIds.has(device.id));

/**
 * requestDevice() filters for one service, narrowed to a remembered device's
 * advertised name so the picker lists just that one. `opts` may be a click
 * event when a pair call is wired straight to onClick.
 */
export const pickerFilters = (service, opts) => {
  const name = typeof opts?.name === "string" && opts.name ? opts.name : null;
  return [name ? { services: [service], name } : { services: [service] }];
};

/**
 * Wait until Chrome hears the device advertise, so it's back in Chrome's
 * cache. After Chrome restarts its cache is empty, and gatt.connect() on a
 * device from getDevices() fails at once with "Bluetooth Device is no longer
 * in range" until a scan has seen it. Resolves true when heard, false at the
 * timeout. A browser without watchAdvertisements() (or that refuses the
 * watch) resolves true, so the caller just tries to connect as before.
 */
export const waitForAdvertisement = (device, timeoutMs) =>
  new Promise((resolve) => {
    if (typeof device?.watchAdvertisements !== "function") {
      resolve(true);
      return;
    }
    const abort = typeof AbortController === "function" ? new AbortController() : null;
    let settled = false;
    let timer = null;
    const onAdvert = () => finish(true);
    const finish = (heard) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      device.removeEventListener("advertisementreceived", onAdvert);
      abort?.abort();
      resolve(heard);
    };
    device.addEventListener("advertisementreceived", onAdvert);
    timer = setTimeout(() => finish(false), timeoutMs);
    Promise.resolve()
      .then(() => device.watchAdvertisements(abort ? { signal: abort.signal } : undefined))
      .catch(() => finish(true));
  });
